import {
  TERMINAL_ORDER_STATUSES,
  TRANSITION_TABLE,
  stateKey,
} from '@fun-xyz/fiat-contract/table'
import type { StateKey } from '@fun-xyz/fiat-contract/table'
import type {
  FiatStepResponse,
  FlowState,
  OrderStatus,
} from '@fun-xyz/fiat-contract/types'

/**
 * Named rather than numbered: the contract's `screens: number[]` index the FE
 * doc (the number trailing each member) — useful for tracing a decision back
 * to its spec, wrong for rendering code to branch on.
 */
export type FiatScreen =
  | 'AMOUNT_ENTRY' // 0 — client-local, before any envelope exists
  | 'QUOTE_REVIEW' // 1
  | 'OTP' // 2
  | 'AUTH_CAPTURE' // provider-rendered session authorization
  | 'KYC_FORM' // 3
  | 'ORDER_REVIEW' // 4
  | 'PAYMENT_CAPTURE' // 5
  | 'BANK_INSTRUCTIONS' // 6
  | 'ORDER_TRACKING' // 7
  | 'KYC_CAPTURE' // 8
  | 'KYC_VERIFYING' // 9
  | 'KYC_ON_HOLD' // 10
  | 'KYC_REJECTED' // 11
  | 'ORDER_PENDING' // 12
  | 'SUCCESS' // 13
  | 'FAILURE' // 14
  | 'REFUND' // 15
  | 'BLOCKED' // 16
  | 'UNSUPPORTED' // no published screen for this state

// Total over the table: a state added to the contract is a compile error
// here until it is mapped.
const SCREEN_BY_STATE: Readonly<Record<StateKey, FiatScreen>> = {
  QUOTE: 'QUOTE_REVIEW',
  // Never renders where identity is ambient, and the docs give it no screen.
  FUN_AUTH: 'UNSUPPORTED',
  SESSION_AUTH: 'OTP',
  'KYC/CAPTURE': 'KYC_CAPTURE',
  'KYC/INPUT_REQUIRED': 'KYC_FORM',
  'KYC/NO_ACTION_REQUIRED:IN_REVIEW': 'KYC_VERIFYING',
  'KYC/NO_ACTION_REQUIRED:ON_HOLD': 'KYC_ON_HOLD',
  'KYC/NO_ACTION_REQUIRED:REJECTED': 'KYC_REJECTED',
  PENDING_ORDER: 'ORDER_PENDING',
  BLOCKED: 'BLOCKED',
  'PAYMENT/CAPTURE': 'PAYMENT_CAPTURE',
  'PAYMENT/INSTRUCT': 'BANK_INSTRUCTIONS',
  'ORDER/AWAITING_CONFIRMATION': 'ORDER_REVIEW',
  // Base of the only fan-out (screens 7/13/14/15); `orderScreen` refines it by status.
  'ORDER/CREATED': 'ORDER_TRACKING',
}

// Unknown and non-terminal statuses both land on tracking — a client that
// stops polling strands the order. Same "non-terminal is the safe default"
// rule the contract applies to CAPTURE_ORPHANED.
const orderScreen = (status: OrderStatus): FiatScreen => {
  if (status === 'SETTLED') {
    return 'SUCCESS'
  }
  if (status === 'REFUNDED') {
    return 'REFUND'
  }
  if (isTerminalStatus(status)) {
    return 'FAILURE'
  } // EXPIRED · CANCELLED · FAILED
  return 'ORDER_TRACKING'
}

const isTerminalStatus = (status: OrderStatus): boolean =>
  TERMINAL_ORDER_STATUSES.some((terminal) => terminal === status)

export interface ComputePageOptions {
  /**
   * Whether a confirmed delivery ends the order.
   *
   * True only where the provider pays the recipient itself: the crypto is
   * already where it was going, so `CRYPTO_SENT` plus its transaction is the
   * last thing that happens, and waiting for the server to say `SETTLED`
   * leaves the user on a spinner over money they have. Where a deposit
   * address still has to be swept, delivery is the middle of the story and
   * this stays off.
   */
  settlesOnDelivery?: boolean
}

export interface FiatPage {
  screen: FiatScreen
  /**
   * Two envelopes with equal identity are the *same* state: a re-entry
   * carrying an error is a banner, not a navigation, and collected form
   * values survive it.
   */
  identity: string
  /** From the table, never inferred from `transitions.length`. */
  terminal: boolean
  /**
   * The client called this order done off its delivery, ahead of the server.
   * Only here is a poll the server still offers worth ignoring: it asks about
   * an order this client has already finished with. Every other terminal page
   * keeps whatever poll the response carries — the contract emits one to
   * recover a failure, and dropping it would strand that recovery.
   */
  settledOnDelivery?: boolean
}

/** The screen shown before any envelope exists. Client-local, has no state key. */
export const AMOUNT_ENTRY_PAGE: FiatPage = {
  screen: 'AMOUNT_ENTRY',
  identity: 'client:AMOUNT_ENTRY',
  terminal: false,
}

/**
 * The envelope, not the bare state: the contract leaves `orderId` placement
 * open (`TODO(open-decision)`), and identity is wrong if the resolver can
 * only see one of the two documented spots.
 */
export type FiatPageInput = Pick<FiatStepResponse, 'state' | 'orderId'>

/**
 * Resolves an envelope to the screen that renders it. Total: a kind this
 * client's table version predates resolves to `UNSUPPORTED`, because the
 * stale rule lets any call return any state and a shipped client has to
 * render something.
 */
export function computePage(
  envelope: FiatPageInput,
  { settlesOnDelivery = false }: ComputePageOptions = {},
): FiatPage {
  const { state } = envelope
  const key = knownStateKey(state)
  if (key === undefined) {
    return unsupportedPage(state)
  }

  const identity = identityOf(key, envelope)

  // OTP is only one session-auth transport. Providers such as MoonPay own
  // account connection in a native surface, so rendering the code screen for
  // that channel would leave the CLIENT_SURFACE transition unreachable.
  //
  // The surface is what makes it a capture, not the channel. Stripe's entry into
  // session auth is provider-rendered too, but carries no surface: there is
  // nothing to render until the client has checked for a Link account and the
  // backend has minted an auth intent. Routing that state here strands it on a
  // screen whose every guard needs a surface, so it falls through to OTP, where
  // the provider's own controller runs that check.
  if (
    state.kind === 'SESSION_AUTH' &&
    state.channel === 'PROVIDER_RENDERED' &&
    state.surface !== undefined
  ) {
    return { screen: 'AUTH_CAPTURE', identity, terminal: false }
  }

  // Narrowed on the union, not the key string, so `status` is known-present.
  if (state.kind === 'ORDER' && state.phase === 'CREATED') {
    // The transaction is required, not just the status: `CRYPTO_SENT` without
    // one names no delivery to have arrived.
    const delivered =
      settlesOnDelivery && state.status === 'CRYPTO_SENT' && state.tx?.hash
    if (delivered) {
      return {
        screen: 'SUCCESS',
        identity,
        terminal: true,
        settledOnDelivery: true,
      }
    }
    return {
      screen: orderScreen(state.status),
      identity,
      terminal: isTerminalStatus(state.status),
    }
  }

  return {
    screen: SCREEN_BY_STATE[key] ?? 'UNSUPPORTED',
    identity,
    terminal: TRANSITION_TABLE[key]?.terminal ?? false,
  }
}

/**
 * Caught rather than pre-validated against a kind list — a list would be a
 * second copy of the contract's switch, free to drift from it.
 */
function knownStateKey(state: FlowState): StateKey | undefined {
  try {
    return stateKey(state)
  } catch {
    return undefined
  }
}

/**
 * Distinct per kind so two unknown states are not treated as the same one, and
 * never terminal — a client that wrongly calls a flow over stops polling.
 */
function unsupportedPage(state: FlowState): FiatPage {
  return {
    screen: 'UNSUPPORTED',
    identity: `unsupported:${String(state.kind)}`,
    terminal: false,
  }
}

/**
 * Two KYC form rounds — or two session rounds, or two orders — share a key and
 * a screen but must not share identity: a second round would silently inherit
 * the first round's typed-in values.
 */
function identityOf(key: StateKey, envelope: FiatPageInput): string {
  const discriminant = discriminantOf(envelope)
  return discriminant === undefined ? key : `${key}:${discriminant}`
}

function discriminantOf({ state, orderId }: FiatPageInput): string | undefined {
  switch (state.kind) {
    case 'QUOTE':
      return state.quote.quoteRef
    // A second round is a different channel, not a repeat of the first: the
    // code typed for the phone must not be carried into the email round. A
    // re-entry on the same channel is the bad-code banner, so it keeps it.
    case 'SESSION_AUTH':
      return state.channel
    case 'KYC':
      return state.phase === 'INPUT_REQUIRED' ? state.form.formId : undefined
    case 'PAYMENT':
      // In-state placement wins; the envelope sibling is the other documented
      // spot for the same id.
      return state.phase === 'INSTRUCT' ? (state.orderId ?? orderId) : undefined
    case 'ORDER':
      return state.phase === 'CREATED'
        ? (state.order?.orderId ?? orderId)
        : state.quote.quoteRef
    default:
      return undefined
  }
}
